import { supabase } from './supabase';

export type MonitoringActivityType =
  | 'all'
  | 'group_post'
  | 'group_post_comment'
  | 'group_chat_message'
  | 'direct_message';

export type MonitoringStatusFilter = 'all' | 'published' | 'hidden' | 'flagged' | 'deleted';

export type MonitoringAction = 'hide' | 'unhide' | 'flag' | 'unflag' | 'delete';

export interface MonitoringFeedItem {
  item_id: string;
  item_type: Exclude<MonitoringActivityType, 'all'>;
  source_table: string;
  group_id: string | null;
  group_name: string | null;
  author_id: string | null;
  author_name: string | null;
  target_user_id: string | null;
  target_user_name: string | null;
  content_preview: string;
  full_content: string | null;
  status: string | null;
  is_flagged: boolean;
  created_at: string;
  moderated_at: string | null;
  moderated_by: string | null;
  moderated_by_name: string | null;
  hidden_at: string | null;
  hidden_by: string | null;
  deleted_at: string | null;
  related_thread_id: string | null;
  media_kind?: 'none' | 'image' | 'video' | 'mixed';
  image_urls?: string[] | null;
  video_url?: string | null;
}

export interface MonitoringFeedResponse {
  items: MonitoringFeedItem[];
  total_count: number;
}

export interface MonitoringFeedParams {
  page: number;
  pageSize: number;
  activityType: MonitoringActivityType;
  status: MonitoringStatusFilter;
  search: string;
  groupSearch: string;
  fromDate: string;
  toDate: string;
}

class SuperAdminMonitoringService {
  async getFeed(params: MonitoringFeedParams): Promise<MonitoringFeedResponse> {
    const { data, error } = await supabase.rpc('get_super_admin_monitoring_feed', {
      p_page: params.page,
      p_page_size: params.pageSize,
      p_activity_type: params.activityType === 'all' ? null : params.activityType,
      p_status: params.status === 'all' ? null : params.status,
      p_search: params.search.trim() || null,
      p_group_search: params.groupSearch.trim() || null,
      p_from_date: params.fromDate || null,
      p_to_date: params.toDate || null,
    });

    if (error) {
      throw new Error(error.message || 'Failed to load monitoring feed.');
    }

    const payload = (data ?? {}) as Partial<MonitoringFeedResponse>;
    return {
      items: Array.isArray(payload.items) ? (payload.items as MonitoringFeedItem[]) : [],
      total_count: typeof payload.total_count === 'number' ? payload.total_count : 0,
    };
  }

  async applyAction(item: MonitoringFeedItem, action: MonitoringAction): Promise<void> {
    const { error } = await supabase.rpc('moderate_super_admin_activity_item', {
      p_source_table: item.source_table,
      p_item_id: item.item_id,
      p_action: action,
    });

    if (error) {
      throw new Error(error.message || `Failed to ${action} item.`);
    }
  }
}

export const superAdminMonitoringService = new SuperAdminMonitoringService();